import mapboxgl from 'mapbox-gl';
import { MapPopup } from './MapPopup';

export class MapClustering {
  constructor(map) {
    this.map = map;
    this.currentPopup = null;
    this.markers = [];
    this.onPointClick = null;
  }

  setPointClickHandler(handler) {
    this.onPointClick = handler;
  }

  initializeClustering(depotsData) {
    this.clearMarkers();

    // Configuration de la source avec clustering
    this.map.addSource('depots', {
      type: 'geojson',
      data: depotsData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    });

    // Couche des clusters
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'depots',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#4CAF50',  // Vert pour < 10 points
          10,
          '#2196F3',  // Bleu pour 10-50 points
          50,
          '#9C27B0'   // Violet pour > 50 points
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          10,
          30,
          50,
          40
        ]
      }
    });

    // Nombre dans les clusters
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'depots',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff'
      }
    });

    // Couche pour les points non clusterisés (avec marqueurs)
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'depots',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 0  // Rendre les points invisibles car on utilise des marqueurs
      }
    });

    // Ajouter les marqueurs pour les points non clusterisés
    this.map.on('sourcedata', (e) => {
      if (e.sourceId !== 'depots' || !e.isSourceLoaded) return;

      this.clearMarkers();
      const features = this.map.querySourceFeatures('depots', {
        filter: ['!', ['has', 'point_count']]
      });

      features.forEach(feature => {
        if (!feature.properties.cluster) {
          const marker = new mapboxgl.Marker()
            .setLngLat(feature.geometry.coordinates);

          marker.getElement().addEventListener('click', () => {
            if (this.currentPopup) {
              this.currentPopup.remove();
            }
            this.currentPopup = MapPopup(feature.geometry.coordinates, feature.properties);
            this.currentPopup.addTo(this.map);
          });

          this.markers.push(marker);
          marker.addTo(this.map);
        }
      });
    });

    // Configuration des événements
    this.setupClusterEvents();
  }

  clearMarkers() {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
  }

  setupClusterEvents() {
    // Clic sur les clusters
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      });
      console.log('Cluster clicked:', features);

      if (!features.length) return;

      const clusterId = features[0].properties.cluster_id;
      this.map.getSource('depots').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        this.map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom
        });
      });
    });

    // Clic sur les points individuels
    this.map.on('click', 'unclustered-point', (e) => {
      console.log('Unclustered point clicked:', e);
      
      if (!e.features || !e.features.length) return;
      
      const coordinates = e.features[0].geometry.coordinates;
      console.log('Coordinates to send:', coordinates);

      // Appeler directement le gestionnaire avec les coordonnées
      if (this.onPointClick) {
        this.onPointClick({
          type: 'point_selected',
          coordinates: coordinates
        });
      }
    });

    // Effets de survol
    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.map.on('mouseenter', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  updateClusters(newData) {
    const source = this.map.getSource('depots');
    if (source) {
      source.setData(newData);
    }

    this.clearMarkers();

    newData.features.forEach(feature => {
      if (!feature.properties.cluster) {
        const marker = new mapboxgl.Marker()
          .setLngLat(feature.geometry.coordinates)
          .addTo(this.map);

        marker.getElement().addEventListener('click', () => {
          if (this.currentPopup) {
            this.currentPopup.remove();
          }
          this.currentPopup = MapPopup(feature.geometry.coordinates, feature.properties);
          this.currentPopup.addTo(this.map);
        });

        this.markers.push(marker);
      }
    });
  }

  removeClustering() {
    // Nettoyer les marqueurs
    this.clearMarkers();

    // Fermer le popup actuel s'il existe
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }

    // Supprimer tous les autres popups par sécurité
    const existingPopups = document.querySelectorAll('.mapboxgl-popup');
    existingPopups.forEach(popup => popup.remove());

    // Supprimer les événements
    this.map.off('click', 'clusters');
    this.map.off('click', 'unclustered-point');
    this.map.off('mouseenter', 'clusters');
    this.map.off('mouseleave', 'clusters');
    this.map.off('mouseenter', 'unclustered-point');
    this.map.off('mouseleave', 'unclustered-point');

    // Supprimer les couches
    if (this.map.getLayer('cluster-count')) {
      this.map.removeLayer('cluster-count');
    }
    if (this.map.getLayer('clusters')) {
      this.map.removeLayer('clusters');
    }
    if (this.map.getLayer('unclustered-point')) {
      this.map.removeLayer('unclustered-point');
    }
    if (this.map.getSource('depots')) {
      this.map.removeSource('depots');
    }
  }
}